import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Plus, Download, Search } from 'lucide-react';
import { useExpenses } from '../hooks/useExpenses';
import { norm } from '../utils/catalog';
import { localDateStr } from '../utils/customers';
import { fmtDate, money } from '../utils/quotes';
import { PAYMENT_METHOD_LABELS, downloadCsv } from '../utils/sales';
import { EXPENSE_CATEGORIES, EXPENSE_METHODS, EXPENSE_STATUS_LABELS, checkExpense, expensesToCsv, isPayableOverdue, payablesSummary } from '../utils/accounting';

const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';
const TONES = {
  pagado: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
  por_pagar: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
  anulado: 'bg-slate-700/40 text-slate-400 border-slate-600/40'
};
const emptyForm = () => ({ status: 'pagado', expense_date: localDateStr(), category: EXPENSE_CATEGORIES[0], description: '', supplier: '', amount: '', payment_method: 'efectivo', paid_at: '', due_date: '', reference: '', notes: '' });

function Field({ label, children }) {
  return <label className="block text-[10px] uppercase font-bold text-seryi-text tracking-wider space-y-1"><span>{label}</span>{children}</label>;
}

function ExpenseForm({ onSave, onCancel }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const paid = form.status === 'pagado';
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkExpense(form);
    if (problem) { setError(problem); return; }
    setError(''); setSaving(true);
    const ok = await onSave(form);
    setSaving(false);
    if (ok) onCancel();
  };
  return (
    <form onSubmit={submit} className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-4" aria-label="Nuevo gasto">
      <div className="flex gap-2" role="radiogroup" aria-label="Estado del gasto">
        {[['pagado', 'Ya lo pagué'], ['por_pagar', 'Lo pagaré después (cuenta por pagar)']].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={form.status === v} onClick={() => setForm((f) => ({ ...f, status: v }))}
            className={`text-xs font-bold px-4 py-2 rounded-xl border ${form.status === v ? 'bg-seryi-green/10 text-seryi-green border-seryi-green/30' : 'border-seryi-bg-light text-seryi-text'}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Qué fue"><input className={inputCls} value={form.description} onChange={set('description')} maxLength={200} placeholder="Ej. Arriendo de octubre" aria-label="Qué fue" /></Field>
        <Field label="Categoría">
          <select className={inputCls} value={form.category} onChange={set('category')} aria-label="Categoría">{EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label="Valor"><input className={inputCls} type="number" min="0" step="0.01" inputMode="decimal" value={form.amount} onChange={set('amount')} aria-label="Valor" /></Field>
        <Field label="Proveedor (opcional)"><input className={inputCls} value={form.supplier} onChange={set('supplier')} maxLength={120} aria-label="Proveedor" /></Field>
        <Field label={paid ? 'Fecha del gasto' : 'Fecha de la factura'}><input className={inputCls} type="date" max={localDateStr()} value={form.expense_date} onChange={set('expense_date')} aria-label="Fecha del gasto" /></Field>
        <Field label="Referencia o factura (opcional)"><input className={inputCls} value={form.reference} onChange={set('reference')} aria-label="Referencia" /></Field>
        {paid ? (
          <>
            <Field label="Cómo se pagó">
              <select className={inputCls} value={form.payment_method} onChange={set('payment_method')} aria-label="Cómo se pagó">{EXPENSE_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </Field>
            <Field label="Fecha de pago (si es otra)"><input className={inputCls} type="date" max={localDateStr()} value={form.paid_at} onChange={set('paid_at')} aria-label="Fecha de pago" /></Field>
          </>
        ) : (
          <Field label="Vence"><input className={inputCls} type="date" value={form.due_date} onChange={set('due_date')} aria-label="Vence" /></Field>
        )}
        <Field label="Notas (opcional)"><input className={inputCls} value={form.notes} onChange={set('notes')} aria-label="Notas" /></Field>
      </div>
      {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-60">{saving ? 'Guardando…' : paid ? 'Guardar gasto' : 'Guardar cuenta por pagar'}</button>
        <button type="button" onClick={onCancel} className="border border-seryi-bg-light text-seryi-text text-xs font-bold px-4 py-2 rounded-xl">Cancelar</button>
      </div>
    </form>
  );
}

// Panel bajo una fila: marcar pagada (medio y fecha) o anular (motivo).
function RowAction({ mode, expense, onPay, onVoid, onClose }) {
  const [method, setMethod] = useState('efectivo');
  const [paidAt, setPaidAt] = useState(localDateStr());
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (mode === 'void' && reason.trim().length < 3) { setError('Escribe el motivo de la anulación.'); return; }
    if (mode === 'pay' && paidAt > localDateStr()) { setError('La fecha de pago no puede ser futura.'); return; }
    setError(''); setBusy(true);
    const ok = mode === 'pay' ? await onPay(expense.id, { payment_method: method, paid_at: paidAt }) : await onVoid(expense.id, reason);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <div className="flex flex-wrap items-end gap-3 p-3 bg-seryi-bg-dark/60">
      {mode === 'pay' ? (
        <>
          <Field label="Cómo se pagó"><select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)} aria-label="Medio de pago">{EXPENSE_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          <Field label="Fecha de pago"><input className={inputCls} type="date" max={localDateStr()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} aria-label="Fecha del pago" /></Field>
        </>
      ) : (
        <div className="flex-1 min-w-[220px]"><Field label="Motivo de la anulación"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo de la anulación" /></Field></div>
      )}
      <button type="button" disabled={busy} onClick={confirm} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-2 rounded-xl disabled:opacity-60">{mode === 'pay' ? 'Confirmar pago' : 'Anular gasto'}</button>
      <button type="button" onClick={onClose} className="text-xs text-seryi-text px-2 py-2">Cancelar</button>
      {error && <p role="alert" className="w-full text-xs text-rose-300">{error}</p>}
    </div>
  );
}

export default function ExpensesPage() {
  const { expenses, loading, tableMissing, createExpense, markPaid, voidExpense } = useExpenses();
  const [tab, setTab] = useState('gastos');
  const [showForm, setShowForm] = useState(false);
  const [action, setAction] = useState(null); // { id, mode }
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [month, setMonth] = useState('');
  const [withVoided, setWithVoided] = useState(false);

  const payables = useMemo(() => payablesSummary(expenses), [expenses]);
  const months = useMemo(() => [...new Set(expenses.map((e) => String(e.expense_date).slice(0, 7)))].sort().reverse(), [expenses]);
  const categories = useMemo(() => [...new Set([...EXPENSE_CATEGORIES, ...expenses.map((e) => e.category)])], [expenses]);

  const filtered = useMemo(() => expenses.filter((e) => {
    if (!withVoided && e.status === 'anulado') return false;
    if (month && String(e.expense_date).slice(0, 7) !== month) return false;
    if (category && e.category !== category) return false;
    const terms = norm(query).split(' ').filter(Boolean);
    if (!terms.length) return true;
    const hay = norm(`${e.description} ${e.supplier ?? ''} ${e.category} ${e.reference ?? ''}`);
    return terms.every((t) => hay.includes(t));
  }), [expenses, query, category, month, withVoided]);

  const total = useMemo(() => filtered.filter((e) => e.status !== 'anulado').reduce((acc, e) => acc + Math.round(Number(e.amount) * 100), 0) / 100, [filtered]);

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261006b_expenses.sql</code> en Supabase para usar los gastos.</div>;
  }

  const rows = tab === 'porpagar' ? payables.rows : filtered;

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Wallet size={20} /> Gastos y cuentas por pagar</h2>
          <p className="text-xs text-seryi-text">Registra lo que gastas y lo que debes a proveedores. La caja y el resultado se calculan solos en <Link to="/v2/caja" className="text-seryi-green hover:underline">Caja y resultado</Link>.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => downloadCsv(`gastos_${localDateStr()}.csv`, expensesToCsv(filtered))} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Exportar gastos</button>
          <button type="button" onClick={() => setShowForm(true)} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-2 rounded-xl flex items-center gap-1.5"><Plus size={13} /> Nuevo gasto</button>
        </div>
      </div>

      {showForm && <ExpenseForm onSave={createExpense} onCancel={() => setShowForm(false)} />}

      <div className="flex gap-2" role="tablist">
        {[['gastos', 'Gastos'], ['porpagar', `Por pagar${payables.count ? ` (${payables.count})` : ''}`]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setAction(null); }}
            className={`text-xs font-bold px-4 py-2 rounded-xl border ${tab === k ? 'bg-seryi-green/10 text-seryi-green border-seryi-green/30' : 'border-seryi-bg-light text-seryi-text'}`}>{label}</button>
        ))}
      </div>

      {tab === 'porpagar' ? (
        <div className="grid grid-cols-3 gap-3">
          {[['Total por pagar', payables.total, 'text-amber-300'], ['Vencido', payables.overdue, 'text-orange-300'], ['Vence en 7 días', payables.soon, 'text-seryi-heading']].map(([l, v, t]) => (
            <div key={l} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4"><p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">{l}</p><p className={`text-lg font-black ${t}`}>{money(v)}</p></div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
            <input className={`${inputCls} pl-8`} placeholder="Buscar por descripción, proveedor o referencia" aria-label="Buscar gastos" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <select aria-label="Filtrar por mes" className={`${inputCls} w-auto`} value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">Todos los meses</option>{months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select aria-label="Filtrar por categoría" className={`${inputCls} w-auto`} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Todas las categorías</option>{categories.map((c) => <option key={c}>{c}</option>)}
          </select>
          <label className="text-[11px] text-seryi-text flex items-center gap-1.5"><input type="checkbox" checked={withVoided} onChange={(e) => setWithVoided(e.target.checked)} /> Ver anulados</label>
          <span className="text-[11px] text-seryi-text">{filtered.length} {filtered.length === 1 ? 'gasto' : 'gastos'} · {money(total)}</span>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-seryi-text">Cargando…</p>
      ) : expenses.length === 0 ? (
        <div className="p-8 text-center text-xs text-seryi-text border border-dashed border-seryi-bg-light rounded-3xl">Aún no hay gastos. Registra el primero con "Nuevo gasto".</div>
      ) : (
        <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
          <table className="w-full text-xs text-seryi-heading">
            <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
              <tr><th className="p-3 text-left">Fecha</th><th className="p-3 text-left">Descripción</th><th className="p-3 text-left">Categoría</th><th className="p-3 text-left">Proveedor</th><th className="p-3 text-left">Estado</th><th className="p-3 text-right">Valor</th><th className="p-3 text-right">Acciones</th></tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <React.Fragment key={e.id}>
                  <tr className={`border-t border-seryi-bg-light ${e.status === 'anulado' ? 'opacity-60' : ''}`}>
                    <td className="p-3 whitespace-nowrap">{fmtDate(e.expense_date)}</td>
                    <td className="p-3 font-semibold">{e.description}{e.reference ? <span className="block text-[10px] text-seryi-text font-normal">Ref. {e.reference}</span> : null}{e.void_reason ? <span className="block text-[10px] text-seryi-text font-normal">Anulado: {e.void_reason}</span> : null}</td>
                    <td className="p-3">{e.category}</td>
                    <td className="p-3">{e.supplier || <span className="text-seryi-text">—</span>}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONES[e.status]}`}>{EXPENSE_STATUS_LABELS[e.status] || e.status}</span>
                        {e.status === 'pagado' && <span className="text-[10px] text-seryi-text">{PAYMENT_METHOD_LABELS[e.payment_method] || ''} · {fmtDate(e.paid_at)}</span>}
                        {e.status === 'por_pagar' && e.due_date && <span className={`text-[10px] ${isPayableOverdue(e) ? 'text-orange-300 font-bold' : 'text-seryi-text'}`}>{isPayableOverdue(e) ? 'Vencida' : 'Vence'} {fmtDate(e.due_date)}</span>}
                      </span>
                    </td>
                    <td className="p-3 text-right font-bold">{money(e.amount)}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {e.status === 'por_pagar' && <button type="button" onClick={() => setAction({ id: e.id, mode: 'pay' })} className="text-seryi-green font-bold mr-3 hover:underline">Marcar pagada</button>}
                      {e.status !== 'anulado' && <button type="button" onClick={() => setAction({ id: e.id, mode: 'void' })} className="text-rose-300 hover:underline">Anular</button>}
                    </td>
                  </tr>
                  {action?.id === e.id && (
                    <tr><td colSpan={7} className="p-0"><RowAction mode={action.mode} expense={e} onPay={markPaid} onVoid={voidExpense} onClose={() => setAction(null)} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-seryi-text">{tab === 'porpagar' ? 'No debes nada a proveedores.' : 'Ningún gasto coincide con el filtro.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
